import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Grid,
    Chip,
    Button,
    Divider,
    Card,
    CardContent,
    Avatar,
    Stack,
    Alert,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemText
} from '@mui/material';
import {
    ArrowBack,
    Event,
    AccessTime,
    Schedule,
    VideoCameraFront,
    Star,
    LocationOn,
    Person,
    Save,
    Link as LinkIcon,
    EditCalendar,
    Download,
    PictureAsPdf,
    TrendingUp,
    Refresh
} from '@mui/icons-material';
import { useBookingDetailsViewModel } from './useBookingDetailsViewModel';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const BookingDetails = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canViewPrice = user?.role === 'superadmin' || user?.role === 'admin';
    const {
        booking,
        loading,
        error,
        remedyData,
        handleRemedyInputChange,
        isSaving,
        saveRemedyData,
        updateStatus,
        remedyPDF,
        loadingPDF,
        downloadPDF,
        rescheduleState,
        viewWhitelabelKundaliPDF,
    } = useBookingDetailsViewModel(id ? parseInt(id) : null);

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8f9fa' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !booking) {
        return (
            <Box sx={{ p: 4, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
                <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
                    Go Back
                </Button>
                <Alert severity="error">
                    {error || 'Booking not found'}
                </Alert>
            </Box>
        );
    }

    // Determine status color
    const getStatusColor = (status: string) => {
        const s = status?.toLowerCase() || '';
        if (s === 'upcoming' || s === 'ongoing') return 'primary';
        if (s === 'completed') return 'success';
        if (s === 'cancelled' || s === 'noshow') return 'error';
        return 'default';
    };

    const buildAccountCard = () => {
        return (
            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Customer
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: '#3b82f6' }}>
                            {booking.account?.name?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography
                                variant="subtitle1"
                                fontWeight="bold"
                                noWrap
                                sx={{
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => navigate(`/account/${booking.account.userId}`)}
                            >
                                {booking.account?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {booking.account?.email}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {booking.account?.mobile || 'No mobile'}
                            </Typography>
                        </Box>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack py={1} direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Preferred Language
                            </Typography>
                            <Typography variant="body2" fontWeight="500">
                                {booking.preferredLanguage}
                            </Typography>
                        </Box>

                        <Link to={`/kundli-charts?userId=${booking.account.userId}`} target="_blank" rel="noopener noreferral">
                            <Button variant="contained">View Kundli</Button>
                        </Link>
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    const buildUserProfileCard = () => {
        return (
            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Subject Details
                    </Typography>

                    {booking.profile && (
                        <Box sx={{ mb: booking.location ? 3 : 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <Person fontSize="small" color="action" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    Astro Profile
                                </Typography>
                            </Stack>

                            <Box sx={{ pl: 3.5 }}>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    gutterBottom
                                    sx={{
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                    onClick={() => navigate(`/user-profiles/${booking.account.userId}/${booking.profile?.id}`)}
                                >
                                    {booking.profile.name}
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">Date of Birth</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {booking.profile.dateTimeOfBirth
                                                ? new Date(booking.profile.dateTimeOfBirth).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                                                : 'Not Specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">Time of Birth</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {booking.profile.dateTimeOfBirth
                                                ? new Date(booking.profile.dateTimeOfBirth).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                : 'Not Specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">Place of Birth</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {booking.profile.placeOfBirth || 'Not Specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">Gender</Typography>
                                        <Typography variant="body2" fontWeight="500" sx={{ textTransform: 'capitalize' }}>
                                            {booking.profile.gender || 'Not Specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">Sun Sign</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {booking.profile.sunSign || 'Not Specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid size={{ xs: 6 }}>
                                        <Typography variant="caption" color="text.secondary" display="block">Moon Sign</Typography>
                                        <Typography variant="body2" fontWeight="500">
                                            {booking.profile.moonSign || 'Not Specified'}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    )}

                    {booking.profile && booking.location && <Divider sx={{ my: 2 }} />}

                    {booking.location && (
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <LocationOn fontSize="small" color="action" />
                                <Typography variant="subtitle2" color="text.secondary">
                                    Vastu Location
                                </Typography>
                            </Stack>
                            <Typography
                                variant="body1"
                                fontWeight="500"
                                sx={{
                                    pl: 3.5,
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                                onClick={() => navigate(`/user-location/${booking.location?.id}`)}
                            >
                                {booking.location.name}
                            </Typography>
                        </Box>
                    )}

                    {booking.profile && (
                        <>
                            <Divider sx={{ my: 2 }} />

                            <Stack py={1} direction="row" justifyContent="end" alignItems="center" spacing={2}>
                                <Link to={`/kundli-charts?profileId=${booking.profile!.id}`} target="_blank" rel="noopener noreferral">
                                    <Button variant="contained">View Kundli</Button>
                                </Link>
                                <Button
                                    variant="outlined"
                                    startIcon={<PictureAsPdf />}
                                    onClick={() => viewWhitelabelKundaliPDF(booking.profile!.id)}
                                    sx={{
                                        borderColor: '#dc2626',
                                        color: '#dc2626',
                                        '&:hover': {
                                            borderColor: '#b91c1c',
                                            backgroundColor: '#fef2f2'
                                        }
                                    }}
                                >
                                    View Full Kundli PDF
                                </Button>
                            </Stack>
                        </>
                    )}

                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ p: 3, backgroundColor: '#f8f9fa', minHeight: '100vh', pb: 8 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBack}
                    sx={{
                        mr: 2,
                        color: '#6b7280',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#e5e7eb' }
                    }}
                >
                    Back
                </Button>
                <Typography variant="h4" fontWeight="bold" color="text.primary">
                    Booking Details
                </Typography>
                <Chip
                    label={booking.status}
                    color={getStatusColor(booking.status)}
                    sx={{
                        ml: 2,
                        textTransform: 'capitalize',
                        fontWeight: 'bold',
                        height: 28
                    }}
                />

                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    {booking.status === 'upcoming' && (
                        <>
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<EditCalendar />}
                                onClick={() => rescheduleState?.openDialog()}
                            >
                                Reschedule
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => updateStatus('cancel')}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                    {((booking.status.toLowerCase() === 'upcoming' && new Date(Date.now()) > booking.scheduledStartTime!) || booking.status.toLowerCase() === "yet_to_mark") && (
                        <>
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => updateStatus('completed')}
                            >
                                Mark Completed
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                onClick={() => updateStatus('noshow')}
                            >
                                Mark No Show
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {/* Main Grid */}
            <Grid container spacing={4}>
                {/* Left Col: Overview, Service, Remedy */}
                <Grid size={{ xs: 12, md: 8 }}>
                    {/* Consultation Info */}
                    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                                <Typography variant="h6" fontWeight="bold">
                                    Consultation Info
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    ID: #{booking.bookingId}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, p: 2, bgcolor: '#f3f4f6', borderRadius: 2 }}>
                                <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 48, height: 48, boxShadow: 1 }}>
                                    <VideoCameraFront />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                                        {booking.consultationType.toUpperCase()} CONSULTATION 
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                                            <Event fontSize="small" />
                                            <Typography variant="body2" fontWeight="500">Date</Typography>
                                        </Stack>
                                        <Typography variant="body1" fontWeight="600">
                                            {booking.scheduledStartTime ? new Date(booking.scheduledStartTime).toLocaleDateString(undefined, {
                                                weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                                            }) : 'Not Scheduled'}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                                            <AccessTime fontSize="small" />
                                            <Typography variant="body2" fontWeight="500">Time</Typography>
                                        </Stack>
                                        <Typography variant="body1" fontWeight="600">
                                            {booking.scheduledStartTime
                                                ? `${new Date(booking.scheduledStartTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                                : 'TBD'}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                {canViewPrice && (
                                    <Grid size={{ xs: 12, sm: 3 }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
                                                <TrendingUp fontSize="small" />
                                                <Typography variant="body2" fontWeight="500">Price</Typography>
                                            </Stack>
                                            <Typography variant="body1" fontWeight="600">
                                                ₹{((booking.price || 0) / 100).toLocaleString()}
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                )}
                            </Grid>

                            {booking.meetingLink && (
                                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Meeting Link
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<LinkIcon />}
                                        href={booking.meetingLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Join Meeting
                                    </Button>
                                </Box>
                            )}

                            {booking.topicOfDiscussion && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Topic of Discussion
                                    </Typography>
                                    <Typography variant="body1">
                                        {booking.topicOfDiscussion}
                                    </Typography>
                                </Box>
                            )}

                            {booking.detailedTopicOfDiscussion && (
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Detailed Topic of Discussion
                                    </Typography>
                                    <Typography variant="body1">
                                        {booking.detailedTopicOfDiscussion}
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Feedback / Rating */}
                    {booking.rating && (
                        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Feedback
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Star sx={{ color: '#fbbf24', fontSize: 28 }} />
                                    <Typography variant="h5" fontWeight="bold">
                                        {booking.rating}
                                        <Typography component="span" variant="body1" color="text.secondary">/5</Typography>
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    )}

                    {/* Remedy Form */}
                    {booking && (booking.status.toLowerCase() !== 'upcoming' && booking.status.toLowerCase() !== 'cancelled' || (booking.status.toLowerCase() === 'upcoming' && booking.scheduledStartTime && new Date() >= booking.scheduledStartTime)) && (
                        <>
                            <Card sx={{ mt: 3, borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Remedy PDF Generator
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Stack spacing={2}>
                                                <TextField
                                                    label="Problems"
                                                    multiline rows={4}
                                                    value={remedyData.problems}
                                                    onChange={(e) => handleRemedyInputChange('problems', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Diagnosis"
                                                    multiline rows={4}
                                                    value={remedyData.diagnosis}
                                                    onChange={(e) => handleRemedyInputChange('diagnosis', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Remedies"
                                                    multiline rows={4}
                                                    value={remedyData.remedies}
                                                    onChange={(e) => handleRemedyInputChange('remedies', e.target.value)}
                                                    fullWidth
                                                />
                                            </Stack>
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 6 }}>
                                            <Stack spacing={2}>
                                                <TextField
                                                    label="Recommended Products"
                                                    multiline rows={4}
                                                    value={remedyData.products}
                                                    onChange={(e) => handleRemedyInputChange('products', e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Reminders"
                                                    multiline rows={8}
                                                    value={remedyData.reminders}
                                                    onChange={(e) => handleRemedyInputChange('reminders', e.target.value)}
                                                    fullWidth
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                            onClick={saveRemedyData}
                                            disabled={isSaving}
                                        >
                                            {isSaving ? 'Saving...' : 'Save Remedy Data'}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                            {/* Remedy PDF Section */}
                            <Card sx={{ mt: 3, borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PictureAsPdf color="error" />
                                        Remedy Report PDF
                                    </Typography>

                                    {loadingPDF ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : !remedyPDF ? (
                                        <Alert severity="info" sx={{ mt: 2 }}>
                                            No remedy PDF generated yet. Fill the data above and save to generate it.
                                        </Alert>
                                    ) : (
                                        <Box 
                                            sx={{ 
                                                mt: 2,
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 2,
                                                bgcolor: '#f9fafb'
                                            }}
                                        >
                                            <PictureAsPdf sx={{ color: '#dc2626', fontSize: 40 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="subtitle1" fontWeight="600">
                                                    Remedy Report PDF
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Last updated: {new Date(remedyPDF.date).toLocaleString()}
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<Download />}
                                                onClick={() => downloadPDF(remedyPDF)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                View PDF
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </>
                    )}
                </Grid>

                {/* Right Col: Customer, Staff */}
                <Grid size={{ xs: 12, md: 4 }}>
                    {buildAccountCard()}

                    {/* Subject Details (Profile & Location) */}
                    {(booking.profile || booking.location) && (
                        buildUserProfileCard()
                    )}

                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>
                                Assigned Expert
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: '#dbeafe', color: '#1e40af', width: 48, height: 48 }}>
                                    {booking.staff?.name?.charAt(0)?.toUpperCase() || 'E'}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {booking.staff?.name || 'Pending Assignment'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Staff ID: {booking.staff?.id}
                                    </Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Reschedule Dialog */}
            <Dialog
                open={!!rescheduleState?.open}
                onClose={() => rescheduleState?.setOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Reschedule Appointment</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            label="Select Date"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={rescheduleState?.selectedDate ? rescheduleState.selectedDate.toISOString().split('T')[0] : ''}
                            inputProps={{ min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0] }}
                            onChange={(e) => rescheduleState?.checkAvailability(new Date(e.target.value))}
                        />

                        {rescheduleState?.loadingSlots ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box sx={{ mt: 3 }}>
                                {rescheduleState?.availableSlots.length > 0 ? (
                                    <>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Available Slots for {rescheduleState.selectedDate?.toLocaleDateString()}
                                        </Typography>
                                        <List sx={{ maxHeight: 300, overflow: 'auto', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            {rescheduleState.availableSlots.map((slot, index) => (
                                                <ListItem key={index} disablePadding>
                                                    <ListItemButton onClick={() => rescheduleState.confirmReschedule(slot)}>
                                                        <ListItemText
                                                            primary={slot.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </>
                                ) : rescheduleState?.selectedDate ? (
                                    <Alert severity="info">No slots available for this date.</Alert>
                                ) : null}
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => rescheduleState?.setOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BookingDetails;
